import { Injectable, NgZone, inject } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { AuthActions } from './auth.actions';
import { AuthService } from '@core/services/auth.service';
import { Router } from '@angular/router';
import { TOKEN } from 'app/app.constants';
import { NotificationFacade } from '@core/facades/notification.facade';
import { IAuthStateModel } from './auth.state.model';
// import { ApprovalService } from '@core/services/approval.service';
import { Pagination } from '@core/models/pagination.model';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
export interface AuthStateModel {
  token: string | null;
  loading: boolean | null;
  isSessionValid: boolean | null;
  tokenExpiredUrl: string | null;
  hasApiError: boolean | null;
  resetPasswordSuccess: boolean | null;
  forgotPasswordSuccess: boolean | null;
  globalPermissions: { [key: string]: string }[];
  auth: IAuthStateModel;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    token: null,
    loading: false,
    isSessionValid: null,
    tokenExpiredUrl: null,
    hasApiError: null,
    resetPasswordSuccess: null,
    forgotPasswordSuccess: null,
    globalPermissions: [],

    auth: {
      token: null,
      loading: false,
      error: null,
      resetPasswordSuccess: null,
      forgotPasswordSuccess: null,
      adminId: null,
      permissions: [],
    },
  },
})
@Injectable()
export class AuthState {
  authService = inject(AuthService);
  rolesAndPermissionsService = inject(RolesAndPermissionsService);
  // constructor(private approvalService: ApprovalService) {}
  store = inject(Store);
  ngZone = inject(NgZone);
  router = inject(Router);
  toast = inject(NotificationFacade);
  static(state: AuthStateModel) {}

  @Action(AuthActions.Verify2FA)
  verify2FA(ctx: StateContext<AuthStateModel>, action: AuthActions.Verify2FA) {
    this.store.dispatch(new AuthActions.SetLoadingStatus(true));

    this.authService.verify2FA(action.payload).subscribe({
      next: (response) => {
        const adminId = response.data.user.id;

        this.store.dispatch(new AuthActions.SetLoadingStatus(false));
        this.store.dispatch(new AuthActions.SetTokenValue(response.token));
        this.store.dispatch(new AuthActions.SetSessionStatus(true));

        // get logged in users permissions
        const permissions = response.data.user.permissions.map(permission => ({ [permission.name]: permission.path }));
        const globalPermissions = response.data.user.global_permissions.map(permission => ({ [permission.name]: permission.path })); // change to global permissions


        // set admin and global permissions to local storage
        localStorage.setItem('userPermissions', JSON.stringify(permissions));
        localStorage.setItem('globalPermissions', JSON.stringify(globalPermissions));

        ctx.patchState({
          token: response.token,
          loading: false,
          isSessionValid: true,
          hasApiError: false,
          auth: {
            ...ctx.getState().auth,
            adminId: adminId,
          },
        });

        this.toast.success('Login successful');
        localStorage.setItem(TOKEN, response.token);
        localStorage.setItem('adminId', adminId);

        this.ngZone.run(async () => this.authService.startSession());
      },
      error: (resp) => {
        this.store.dispatch(new AuthActions.SetLoadingStatus(false));
        ctx.patchState({
          token: null,
          loading: false,
          isSessionValid: false,
          tokenExpiredUrl: null,
          hasApiError: null,
        });
      },
    });
  }

    
  @Action(AuthActions.Login)
  login(ctx: StateContext<AuthStateModel>, action: AuthActions.Login) {
    this.store.dispatch(new AuthActions.SetLoadingStatus(true));

    const { email, password } = action.payload;
    this.authService.login({ email, password }).subscribe({
      next: (response) => {
        localStorage.setItem('2FA-token', response.token);
        if (response.two_factor_enabled) {
          // Navigate to verify OTP page
          this.ngZone.run(async () =>
            this.router.navigateByUrl('/auth/login/2fa')
          );
          return;
        } else {
          // Navigate to setup 2FA page
          this.ngZone.run(async () =>
            this.router.navigateByUrl('/auth/login/setup')
          );
          return;
        }
      },
    });
  }

  @Action(AuthActions.LoadPermissions)
  loadPermissions(ctx: StateContext<AuthStateModel>) {
    const adminId = localStorage.getItem('adminId');
    if (adminId) {
      // get permissions based on roleId and patch state with all permissions available to the user
      const permissionsSub = this.rolesAndPermissionsService.getAdminPermissionById(adminId).subscribe({
          next: (roleResponse) => {
            const permissions = roleResponse.data.map(permission => ({ [permission.name]: permission.path }));
            // set admin permissions to local storage
            localStorage.setItem('userPermissions', JSON.stringify(permissions));
          }
        });

      // get and set global permissions
      const globalPermissionsSub = this.rolesAndPermissionsService.getAllPermissions().subscribe({
        next: (allPermissionsResponse) => {
          const allPermissions = allPermissionsResponse.data.map(permission => ({ [permission.name]: permission.path })) 
          localStorage.setItem('globalPermissions', JSON.stringify(allPermissions));
        }
      });
    }
  }

  @Action(AuthActions.SetLoadingStatus)
  setLoadingStatus(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetLoadingStatus
  ) {
    ctx.patchState({
      loading: action.loading,
    });
  }

  @Action(AuthActions.SetSessionStatus)
  setSessionStatus(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetSessionStatus
  ) {
    ctx.patchState({
      token: null,
      isSessionValid: action.payload,
    });
  }

  @Action(AuthActions.SetTokenExpiredUrl)
  setTokenExpiredUrl(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetTokenExpiredUrl
  ) {
    ctx.patchState({
      tokenExpiredUrl: action.payload,
    });
  }

  @Action(AuthActions.ResetState)
  resetAuthState(ctx: StateContext<AuthStateModel>) {
    ctx.patchState({
      token: null,
      loading: false,
      isSessionValid: false,
      tokenExpiredUrl: null,
      hasApiError: null,
    });
  }

  @Action(AuthActions.SetAdminId)
  setAdminId(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetAdminId
  ) {
    ctx.patchState({
      auth: {
        ...ctx.getState().auth,
        adminId: action.payload,
      },
    });
  }

  @Action(AuthActions.SetPermissions)
  setPermissions(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetPermissions
  ) {
    ctx.patchState({
      auth: {
        ...ctx.getState().auth,
        permissions: action.payload.permissions,
      },
    });
  }

  @Action(AuthActions.SetGlobalPermissions)
  setGlobalPermissions(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetGlobalPermissions
  ) {
    ctx.patchState({
      globalPermissions: action.payload.globalPermissions,
    });
  }

  @Action(AuthActions.SetTokenValue)
  setTokenValue(
    ctx: StateContext<AuthStateModel>,
    action: AuthActions.SetTokenValue
  ) {
    ctx.patchState({
      token: action.token,
    });
  }
}
